import { useEffect, useState, useMemo } from "react";
import CustomCard from "../../components/CustomCard/CustomCard";
import { useSelector, useDispatch } from "react-redux";
import useSearchableTable from "../../hooks/useSearchableTable";
import TableHeader from "../../components/CustomTable/TableHeader";
import { getBusEnt, getCreditLimitRequests } from "../../redux/slices/creditLimitSlice";
import { getStatusList } from "../../redux/slices/EnqMngtSlice";
import CreditLimitTable from "./Components/CreditLimitTable";
import { capitalizeEntireWord, downloadPageHandle, generateStatusTableTitle } from "../../helpers/helpers";
import { CREDIT_LIMIT_MSGS } from "../../constants/constants";
import {
  CREDIT_STATUS_DISPLAY_NAMES,
  ENT_STATUS,
  TITLE_TXT,
  BTN_TXT,
  FORM_MODAL_WIDTHS,
} from "../../constants/constants";
import CustomModal from "../../components/CustomModal/CustomModal";
import "./CreditLimit.scss";
import NewCreditLimitForm from "./Components/NewCreditLimitForm";
import CreditLimitRequestsTable from "./Components/CreditLimitRequestsTable";
import { PERM_CODE, CREDIT_STATUS_PRIVILEGE_MAP } from "../../constants/PermissionCodes";
import { Spin, Tabs, message } from "antd";
import { count } from "../../redux/slices/dealsSlice";

const buildStatusTableTitle = ({
  selectedStatus,
  statusDisplayNames = {},
  statusListWithCounts = [],
  defaultTitle = "",
}) => {
  const getCount = (id) => {
    const item = statusListWithCounts?.find((s) => s.statusId === id);
    return Number(item?.count) || 0;
  };

  if (Array.isArray(selectedStatus)) {
    if (selectedStatus.length === 0) {
      return `${capitalizeEntireWord(defaultTitle) || defaultTitle} LIST (0)`;
    }
    return selectedStatus
      .map((id, index) => {
        const rawName = statusDisplayNames?.[id] || id;
        const name = capitalizeEntireWord(rawName) || rawName || "";
        const count = getCount(id);
        const isLast = index === selectedStatus.length - 1;
        return isLast ? `${name} LIST (${count})` : `${name} (${count})`;
      })
      .join(", ");
  }

  if (selectedStatus) {
    const rawName = statusDisplayNames?.[selectedStatus] || selectedStatus || defaultTitle;
    const name = capitalizeEntireWord(rawName) || rawName || "";
    const count = getCount(selectedStatus);
    return `${name} LIST (${count})`;
  }

  return `${capitalizeEntireWord(defaultTitle) || defaultTitle} LIST (0)`;
};

const CreditLimit = () => {
  const dispatch = useDispatch();
  const { creditLimitData, requestsData, loading, error } = useSelector((state) => state?.creditLimit);
  const { statusList } = useSelector((state) => state?.enqMngt);
  const { user, allCountData } = useSelector((state) => state.auth);
  const { CountData } = useSelector((state) => state?.deals);
  const { loading: docLoading, error: docError } = useSelector((state) => state.documents);

  const [selectedStatus, setSelectedStatus] = useState(null);
  const [modal, setModal] = useState(false);
  const [columnControl, setColumnControl] = useState(null);

  // Derive user privilege codes
  const userPrivileges = useMemo(() => {
    const raw = user?.privilege || [];
    return raw
      .map((p) => (typeof p === "string" ? p : p?.privilegeCode))
      .filter(Boolean);
  }, [user]);

  // Compute allowed statuses for this user
  const allowedStatusIds = useMemo(
    () => userPrivileges.flatMap((priv) => CREDIT_STATUS_PRIVILEGE_MAP[priv] || []),
    [userPrivileges]
  );

const DEFAULT_CREDIT_STATUSES = [
  ENT_STATUS.PENDING.ID,
  ENT_STATUS.ACTIVE.ID,
];

  // Fetch statuses + pick default
  useEffect(() => {
    const fetchStatus = async () => {
      const res = await dispatch(getStatusList("busEnt"));
      if (res.payload?.statusCode === 200) {
        const preferredStatuses = DEFAULT_CREDIT_STATUSES.filter((id) =>
          allowedStatusIds.includes(id),
        );
        setSelectedStatus(
          preferredStatuses.length > 0
            ? preferredStatuses
            : allowedStatusIds[0]
            ? [allowedStatusIds[0]]
            : [],
        );
      }
    };
    fetchStatus();
  }, [dispatch, allowedStatusIds]);

  // Fetch credit-limit data for selected status
  useEffect(() => {
    if (selectedStatus) {
      dispatch(getBusEnt(selectedStatus)).finally(() => {
        dispatch(count("CREDITLIMIT"));
      });
    }
  }, [dispatch, selectedStatus]);

  // Fetch deals count and credit limit requests once
  useEffect(() => {
    dispatch(count("CREDITLIMIT"));
    dispatch(getCreditLimitRequests());
  }, [dispatch]);

  // Table search/filter hook
  const { searchText, setSearchText, filteredData } =
    useSearchableTable(creditLimitData);

  const handleStatusChange = (value) => {
    setSelectedStatus(value);
  };

  // Limit dropdown statuses by privileges
  const filteredStatusList = statusList?.filter((status) =>
    allowedStatusIds.includes(status.statusId)
  );

  // Merge counts **only for IsDeal statuses**
  const isDealStatusIds = Object.values(ENT_STATUS).map((s) => s.ID);
  const listCount = Array.isArray(creditLimitData) ? creditLimitData.length : null;
  const statusListWithCounts = filteredStatusList.map((item) => {
    const isDeal = isDealStatusIds.includes(item.statusId);
    let count = isDeal ? CountData?.find((c) => c.statusId === item.statusId)?.count || 0 : undefined;

    // Keep badge/list in sync once the table data has loaded (only for single selected status)
    if (
      (!Array.isArray(selectedStatus) || selectedStatus.length === 1) &&
      (Array.isArray(selectedStatus) ? selectedStatus.includes(item.statusId) : item.statusId === selectedStatus) &&
      listCount !== null &&
      !loading
    ) {
      count = listCount;
    }

    return {
      ...item,
      count: isDeal ? count : undefined,
    };
  });

  const tableTitle = useMemo(
    () =>
      (generateStatusTableTitle || buildStatusTableTitle)({
        selectedStatus,
        statusDisplayNames: CREDIT_STATUS_DISPLAY_NAMES,
        statusListWithCounts,
        defaultTitle: "Credit Limit",
      }),
    [selectedStatus, statusListWithCounts],
  );

  // ============================
  // ✅ DOWNLOAD HANDLERS
  // ============================

  const handleDownload = (type) => downloadPageHandle({ dispatch, type, screenName: "creditLimit", statusId: selectedStatus });

  useEffect(() => {
    if (error) {
      message.error(
        typeof error === "string"
          ? error
          : "Unable to load credit limit records. Please refresh the page.",
      );
    }
  }, [error]);

  useEffect(() => {
    if (docError) {
      message.error(CREDIT_LIMIT_MSGS.DOWNLOAD_FAILED);
    }
  }, [docError]);

  const requestsCount = requestsData?.length || 0;
  const creditLimitTotalCount = useMemo(() => {
    if (allCountData?.creditLimit !== undefined && allCountData?.creditLimit !== null) {
      return Number(allCountData.creditLimit) || 0;
    }
    if (Array.isArray(CountData) && CountData.length > 0) {
      const dealCounts = CountData.filter((c) => isDealStatusIds.includes(c.statusId));
      if (dealCounts.length > 0) {
        return dealCounts.reduce((acc, curr) => acc + (Number(curr.count) || 0), 0);
      }
    }
    return Array.isArray(creditLimitData) ? creditLimitData.length : 0;
  }, [allCountData, CountData, isDealStatusIds, creditLimitData]);

  const tabItems = [
    {
      key: "requests",
      label: `Request (${requestsCount})`,
      children: (
        <CreditLimitRequestsTable selectedExistingStatus={selectedStatus} />
      ),
    },
    {
      key: "creditLimit",
      label: `Credit Limit (${creditLimitTotalCount})`,
      forceRender: true,
      children: (
        <Spin spinning={loading}>
          <TableHeader
            title={tableTitle}
            searchText={searchText}
            setSearchText={setSearchText}
            isDropdown={true}
            dropdownData={statusListWithCounts}
            selectedStatus={selectedStatus}
            handleStatusChange={handleStatusChange}
            statusDisplayNames={CREDIT_STATUS_DISPLAY_NAMES}
            btnName={userPrivileges.includes(PERM_CODE.CAACC) ? BTN_TXT.ADD_NEW_CREDIT_LIMIT : undefined}
            handleBtnOnClick={() => setModal(true)}
            isDownloadRequired={true}
            onDownloadPdf={() => handleDownload("pdf")}     // ✅
            onDownloadExcel={() => handleDownload("excel")} // ✅
            downloadLoading={docLoading}
            columnControl={columnControl}
          />

          <CreditLimitTable
            tableData={filteredData}
            selectedStatus={selectedStatus}
            onColumnControlChange={setColumnControl}
          />

          {modal && (
            <CustomModal
              title={TITLE_TXT.ADD_NEW_CREDIT_LIMIT}
              open
              centered
              width={FORM_MODAL_WIDTHS}
              content={
                <NewCreditLimitForm
                  onClose={() => setModal(false)}
                  selectedStatus={selectedStatus}
                />
              }
            />
          )}
        </Spin>
      ),
    },
  ];

  return (
    <CustomCard>
      <Tabs defaultActiveKey="requests" items={tabItems} className="credit-limit-tabs" />
    </CustomCard>
  );
};

export default CreditLimit;
